import * as React from "react";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import ErrorBanner from "../../components/ErrorBanner";
import DeleteDialog from "../../components/DeleteDialog";
import ImportRateGroupMappingModel from "./ImportRateGroupMappingModel";

import {
  useQueryGetRateGroupMappingPage,
  useMutatePostRateGroupMapping,
  useMutateDeleteRateGroupMapping,
  useMutatePutRateGroupMapping,
} from "../../data/services/benefitPlanService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";

const InputCell = (props) => {
  const { dataItem, field } = props;
  return (
    <td>
      {dataItem.inEdit ? (
        <input
          className="k-textbox k-input k-rounded-md"
          value={dataItem[field] || ""}
          onChange={(e) =>
            props.onChange({ dataItem, field, value: e.target.value })
          }
        />
      ) : (
        dataItem[field]
      )}
    </td>
  );
};

const CarrierCell = ({ dataItem, field, onChange, carrierList }) => (
  <td>
    {dataItem.inEdit ? (
      <DropDownList
        data={carrierList}
        textField="carrierName"
        value={carrierList.find((x) => x.carrierName === dataItem.carrierName)}
        onChange={(e) =>
          onChange({ dataItem, field, value: e.target.value.carrierName })
        }
      />
    ) : (
      dataItem.carrierName
    )}
  </td>
);

const RateGroupMapping = () => {
  const editField = "inEdit";
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
    sort: [],
  });
  const page = Math.floor(gridDataState.skip / gridDataState.take) + 1;

  const [data, setData] = React.useState([]);
  const [isInAddMode, setIsInAddMode] = React.useState(false);
  const [showDeleteModel, setShowDeleteModel] = React.useState(false);
  const [deleteItem, setDeleteItem] = React.useState();
  const [showImportModel, setShowImportModel] = React.useState(false);

  const {
    isLoading,
    data: apiResp,
    isFetching,
  } = useQueryGetRateGroupMappingPage(
    page,
    gridDataState.take,
    gridDataState.sort,
  );
  const pagedData = apiResp?.data;

  const { data: carrierResp } = useQueryGetCarrierList();
  const carrierList = carrierResp?.data || [];

  const postMutation = useMutatePostRateGroupMapping();
  const putMutation = useMutatePutRateGroupMapping();
  const deleteMutation = useMutateDeleteRateGroupMapping();

  React.useEffect(() => {
    if (!isLoading && pagedData?.items) {
      setData(pagedData.items.map((item) => ({ ...item, [editField]: false })));
      setIsInAddMode(false);
    }
  }, [isLoading, pagedData]);

  const itemChange = (event) => {
    const field = event.field || "";
    const newData = data.map((item) =>
      item.bpRateGroupMappingId === event.dataItem.bpRateGroupMappingId ||
      (isInAddMode && item === event.dataItem)
        ? { ...item, [field]: event.value }
        : item,
    );
    setData(newData);
  };

  const handleSave = async (dataItem) => {
    const carrierId = carrierList.find(
      (e) => e.carrierName === dataItem.carrierName,
    )?.carrierId;
    const payload = { ...dataItem, carrierId };
    if (dataItem.bpRateGroupMappingId) {
      await putMutation.mutateAsync(payload);
    } else {
      await postMutation.mutateAsync(payload);
    }
  };

  const CommandCell = (props) => {
    const inEdit = props.dataItem[editField];
    const isNew = props.dataItem.bpRateGroupMappingId === undefined;

    const onCancel = () => {
      if (isNew) {
        setData(data.slice(1));
        setIsInAddMode(false);
      } else {
        const original = pagedData.items.find(
          (i) => i.bpRateGroupMappingId === props.dataItem.bpRateGroupMappingId,
        );
        setData(
          data.map((i) =>
            i.bpRateGroupMappingId === props.dataItem.bpRateGroupMappingId
              ? { ...original, inEdit: false }
              : i,
          ),
        );
      }
    };

    return (
      <td className="k-command-cell">
        {inEdit ? (
          <>
            <Button
              onClick={() => handleSave(props.dataItem)}
              themeColor="primary"
              style={{ marginRight: "5px" }}
            >
              {isNew ? "Add" : "Update"}
            </Button>
            <Button onClick={onCancel}>{isNew ? "Discard" : "Cancel"}</Button>
          </>
        ) : (
          <>
            <Button
              disabled={isInAddMode}
              onClick={() =>
                setData(
                  data.map((i) =>
                    i.bpRateGroupMappingId ===
                    props.dataItem.bpRateGroupMappingId
                      ? { ...i, inEdit: true }
                      : i,
                  ),
                )
              }
              style={{ marginRight: "5px" }}
            >
              Edit
            </Button>
            <Button
              disabled={isInAddMode}
              onClick={() => {
                setDeleteItem(props.dataItem);
                setShowDeleteModel(true);
              }}
            >
              Remove
            </Button>
          </>
        )}
      </td>
    );
  };

  return (
    <>
      <ErrorBanner handler={postMutation} />
      <ErrorBanner handler={putMutation} />
      <ErrorBanner handler={deleteMutation} />

      <Grid
        style={{ height: "700px" }}
        data={data}
        total={pagedData?.totalCount || 0}
        {...gridDataState}
        onDataStateChange={(e) => setGridDataState(e.dataState)}
        onItemChange={itemChange}
        editField={editField}
        pageable={{
          buttonCount: 5,
          info: true,
          type: "numeric",
          pageSizes: [10, 20, 50, 100],
        }}
        sortable={{ allowUnsort: true, mode: "multiple" }}
        loading={isFetching}
        rowRender={(tr, props) =>
          React.cloneElement(tr, {
            style: props.dataItem[editField]
              ? { backgroundColor: "#fffdaf" }
              : {},
          })
        }
      >
        <GridToolbar>
          <Button
            themeColor="primary"
            onClick={() => {
              setData([
                {
                  [editField]: true,
                  carrierName: "",
                  planGroup: "",
                  rateFactorName: "",
                  rateGroup: "",
                },
                ...data,
              ]);
              setIsInAddMode(true);
            }}
            disabled={isInAddMode}
          >
            Add new
          </Button>
          <Button
            themeColor="info"
            onClick={() => setShowImportModel(true)}
            style={{ marginLeft: "10px" }}
            disabled={isInAddMode}
          >
            Import Mapping
          </Button>
        </GridToolbar>

        <Column
          field="carrierName"
          title="Carrier"
          width="200px"
          cell={(props) => <CarrierCell {...props} carrierList={carrierList} />}
        />
        <Column field="planGroup" title="Plan Group" cell={InputCell} />
        <Column
          field="rateFactorName"
          title="Rate Factor Name"
          cell={InputCell}
        />
        <Column
          field="rateGroup"
          title="Prism Billing Rate Group"
          cell={InputCell}
        />
        <Column cell={CommandCell} width="180px" sortable={false} />
      </Grid>

      {showDeleteModel && (
        <DeleteDialog
          onDelete={async (item) => {
            await deleteMutation.mutateAsync(item);
            setShowDeleteModel(false);
          }}
          onCancel={() => setShowDeleteModel(false)}
          item={deleteItem}
        />
      )}
      {showImportModel && (
        <ImportRateGroupMappingModel
          onClose={() => setShowImportModel(false)}
          existingMappings={pagedData?.items || []}
          carrierList={carrierList}
        />
      )}
    </>
  );
};

export default RateGroupMapping;
